import api from '../axiosConfig';

/* ================================================================
   ControlObraService (Fase 1)
   Base URL backend: /control-obra  (desacoplado de presupuestos)
   ================================================================ */

const BASE = '/control-obra';

const unwrap = (res) => {
  const payload = res?.data ?? {};
  return payload && typeof payload === 'object' && 'data' in payload
    ? (payload.data ?? {})
    : payload;
};

const rutaAdjuntos = (destino, id) => {
  if (destino === 'inconveniente') return `${BASE}/inconvenientes/${id}/adjuntos`;
  if (destino === 'comentario') return `${BASE}/comentarios/${id}/adjuntos`;
  if (destino === 'presupuesto') return `${BASE}/presupuestos/${id}/adjuntos`;
  return `${BASE}/${id}/adjuntos`;
};

const ControlObraService = {
  /* ---------- Obras ---------- */
  crearObra: async (data) => {
    const res = await api.post(`${BASE}`, data);
    if (res.status === 200 || res.status === 201) return unwrap(res);
    throw new Error('No se pudo crear la obra');
  },

  listarObras: async (filters = {}) => {
    const params = {};
    if (filters.empresa_id) params.empresa_id = filters.empresa_id;
    if (filters.estado) params.estado = filters.estado;
    if (filters.proyecto_id) params.proyecto_id = filters.proyecto_id;
    const res = await api.get(`${BASE}`, { params });
    if (res.status !== 200) throw new Error('Error al listar obras');
    return {
      ok: !!res.data?.ok,
      items: Array.isArray(res.data?.items) ? res.data.items : [],
      total: Number(res.data?.total ?? 0),
    };
  },

  // Vista previa del import: sólo extrae. Devuelve { rubros, warnings, total_rubros,
  // total_tareas, total_monto, titulo_sugerido } sin crear la obra. Confirmar reenvía
  // `rubros` a crearObra con origen 'import', así el archivo se lee una sola vez.
  // La extracción con IA puede tardar: el caller debería mostrar progreso.
  importarObraPreview: async (formData) => unwrap(await api.post(`${BASE}/importar/preview`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  })),

  /**
   * El bucle de revisión del import.
   *
   * `modo: 'corregir'` manda sólo la estructura ya extraída — rápido, y no puede
   * inventar filas que no estaban. `modo: 'releer'` manda además el archivo, para
   * cuando la corrección necesita algo que la primera lectura ignoró.
   */
  importarObraRefinar: async ({ estructura, respuestas = {}, instruccion = null, modo = 'corregir', archivo = null, sheet = null, niveles = null }) => {
    const fd = new FormData();
    fd.append('modo', modo);
    fd.append('estructura', JSON.stringify(estructura || []));
    fd.append('respuestas', JSON.stringify(respuestas || {}));
    if (instruccion) fd.append('instruccion', instruccion);
    if (sheet) fd.append('sheet', sheet);
    if (niveles) fd.append('niveles', String(niveles));
    if (modo === 'releer' && archivo) fd.append('archivo', archivo);
    return unwrap(await api.post(`${BASE}/importar/refinar`, fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }));
  },

  /**
   * La vista de revisión de un árbol ya extraído: el árbol achatado, los totales
   * y el desglose por rubro.
   *
   * Es el mismo achatado que devuelve una lectura —sale de `vistaDeRevision`, que
   * corre la normalización que después usa `crearObra`— y por eso se le puede
   * pedir para el árbol FUSIONADO de varias planillas sin escribir una segunda
   * versión del recorrido. Determinista y sin modelo: no cuesta ni tarda.
   */
  importarObraRevision: async ({ estructura, categorias_costo = null }) =>
    unwrap(await api.post(`${BASE}/importar/revision`, {
      estructura,
      ...(categorias_costo?.length ? { categorias_costo } : {}),
    })),

  /**
   * El cronograma del alta a partir del criterio de plazos en palabras.
   *
   * `rubros` son `{ clave, nombre, peso }` —peso es la plata, puede ser 0— y
   * vuelve `{ tramos: [{ clave, dias_offset, dias_dura }], warnings }`. Los
   * tramos pueden solaparse, y un rubro que no vino queda sin tramo: lo completa
   * la pantalla, el backend no lo inventa.
   */
  altaCronograma: async ({ rubros, inicio, dias, criterio, empresa_id = null }) =>
    unwrap(await api.post(`${BASE}/alta/cronograma`, { rubros, inicio, dias, criterio, empresa_id })),

  /** Qué imputaría el matcher sobre lo ya cargado en la caja, sin escribir nada. */
  imputacionesSugeridas: async (obraId, empresa_id, tipo = 'egreso') =>
    unwrap(await api.get(`${BASE}/${obraId}/imputaciones-sugeridas`, { params: { empresa_id, tipo } })),

  obtenerObra: async (id, empresa_id) => {
    const res = await api.get(`${BASE}/${id}`, { params: { empresa_id } });
    if (res.status === 200) return unwrap(res);
    throw new Error('Error al obtener la obra');
  },

  actualizarObra: async (id, empresa_id, cambios) => unwrap(await api.patch(`${BASE}/${id}`, { empresa_id, cambios })),
  /**
   * Los presupuestos nacidos de obras, de todas las obras a la vez.
   *
   * Es lo que consume la pestaña de Control de Presupuestos. El listado normal
   * de presupuestos los filtra a propósito —una obra puede aportar 300 filas a
   * una tabla plana—, así que se ven por su propia puerta.
   *
   * Devuelve `{ items, obras }`: cada item ya trae `nodo_nombre` y `nodo_ruta`
   * resueltos contra el árbol, y `huerfano` si el nodo del que colgaba ya no está.
   */
  /* ---------- Avance desde un documento ---------- */

  /**
   * Lee un Excel, un PDF/foto o un texto y devuelve QUÉ ENTENDIÓ. No escribe nada.
   *
   * Extraer y aplicar son dos actos: entre uno y otro va la pregunta de si además
   * se arma el certificado, y esa se hace con lo leído a la vista.
   */
  leerAvanceDeDocumento: async (obraId, empresa_id, { archivo = null, texto = null } = {}) => {
    const fd = new FormData();
    fd.append('empresa_id', empresa_id);
    if (archivo) fd.append('archivo', archivo);
    if (texto) fd.append('texto', texto);
    const res = await api.post(`${BASE}/${obraId}/avance/leer`, fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    if (res.status !== 200) throw new Error('No se pudo leer el documento');
    return unwrap(res);
  },

  aplicarAvanceDeDocumento: async (obraId, empresa_id, { lineas, certificar = false }) =>
    unwrap(await api.post(`${BASE}/${obraId}/avance/aplicar`, { empresa_id, lineas, certificar })),

  /* ---------- La plata de un nodo, como lista de presupuestos ---------- */

  /**
   * Los presupuestos colgados de un nodo — los dos lados.
   *
   * El costo de un nodo dejó de ser dos campos y pasó a ser esto: N egresos, cada
   * uno con su categoría y su proveedor. El total es la suma.
   */
  presupuestosDeNodo: async (obraId, empresa_id, nodo_uid) => {
    const res = await api.get(`${BASE}/${obraId}/presupuestos`, { params: { empresa_id, nodo_uid } });
    if (res.status !== 200) throw new Error('Error al traer los presupuestos');
    return Array.isArray(res.data?.items) ? res.data.items : [];
  },

  crearPresupuestoDeNodo: async (obraId, empresa_id, data) =>
    unwrap(await api.post(`${BASE}/${obraId}/presupuestos`, { empresa_id, ...data })),

  editarPresupuestoDeNodo: async (obraId, presupuestoId, empresa_id, cambios) =>
    unwrap(await api.patch(`${BASE}/${obraId}/presupuestos/${presupuestoId}`, { empresa_id, ...cambios })),

  borrarPresupuestoDeNodo: async (obraId, presupuestoId, empresa_id) =>
    unwrap(await api.delete(`${BASE}/${obraId}/presupuestos/${presupuestoId}`, { params: { empresa_id } })),

  crearContingencia: async (obraId, presupuestoId, empresa_id, data) =>
    unwrap(await api.post(`${BASE}/${obraId}/presupuestos/${presupuestoId}/contingencias`, { empresa_id, ...data })),

  editarContingencia: async (obraId, presupuestoId, contingenciaId, empresa_id, cambios) =>
    unwrap(await api.patch(`${BASE}/${obraId}/presupuestos/${presupuestoId}/contingencias/${contingenciaId}`, { empresa_id, ...cambios })),

  borrarContingencia: async (obraId, presupuestoId, contingenciaId, empresa_id) =>
    unwrap(await api.delete(`${BASE}/${obraId}/presupuestos/${presupuestoId}/contingencias/${contingenciaId}`, { params: { empresa_id } })),

  reemplazarLineas: async (obraId, presupuestoId, empresa_id, lineas) =>
    unwrap(await api.put(`${BASE}/${obraId}/presupuestos/${presupuestoId}/lineas`, { empresa_id, lineas })),

  crearAdicional: async (obraId, presupuestoId, empresa_id, data) =>
    unwrap(await api.post(`${BASE}/${obraId}/presupuestos/${presupuestoId}/adicionales`, { empresa_id, ...data })),

  editarAdicional: async (obraId, presupuestoId, adicionalId, empresa_id, cambios) =>
    unwrap(await api.patch(`${BASE}/${obraId}/presupuestos/${presupuestoId}/adicionales/${adicionalId}`, { empresa_id, ...cambios })),

  borrarAdicional: async (obraId, presupuestoId, adicionalId, empresa_id) =>
    unwrap(await api.delete(`${BASE}/${obraId}/presupuestos/${presupuestoId}/adicionales/${adicionalId}`, { params: { empresa_id } })),

  crearAdicionalDeDosLados: async (obraId, empresa_id, data) =>
    unwrap(await api.post(`${BASE}/${obraId}/adicionales`, { empresa_id, ...data })),

  editarAdicionalDeDosLados: async (obraId, grupoId, empresa_id, cambios) =>
    unwrap(await api.patch(`${BASE}/${obraId}/adicionales/${grupoId}`, { empresa_id, ...cambios })),

  borrarAdicionalDeDosLados: async (obraId, grupoId, empresa_id) =>
    unwrap(await api.delete(`${BASE}/${obraId}/adicionales/${grupoId}`, { params: { empresa_id } })),

  presupuestosDeObras: async (empresa_id, params = {}) => {
    const res = await api.get(`${BASE}/presupuestos`, { params: { empresa_id, ...params } });
    return { items: res.data?.items || [], obras: res.data?.obras || [] };
  },
  /**
   * La plata de la obra por categoría: declarado (lo que cuelga de la obra
   * entera) vs ubicado (lo que ya está en un nodo). No valida nada — muestra
   * los dos números y la diferencia.
   */
  presupuestosPorCategoria: async (id, empresa_id) => {
    const res = await api.get(`${BASE}/${id}/presupuestos/por-categoria`, { params: { empresa_id } });
    return res.data?.items || [];
  },
  /**
   * Las opciones del selector de nodo: la obra primero y después el árbol, cada
   * uno con su ruta. La ruta es lo único que distingue dos nodos que se llaman
   * parecido en ramas distintas.
   */
  nodosDeObra: async (id, empresa_id) => {
    const res = await api.get(`${BASE}/${id}/nodos`, { params: { empresa_id } });
    return { obra: res.data?.obra || null, nodos: res.data?.nodos || [] };
  },
  /**
   * Cuelga un presupuesto de un nodo. `crear` pide un nodo nuevo primero: se
   * crea por la puerta de la obra —nunca inventando un uid de este lado— y
   * recién después se vincula.
   */
  vincularPresupuesto: async (id, presupuestoId, empresa_id, { nodo_uid = null, tipo = null, crear = null } = {}) => unwrap(
    await api.post(`${BASE}/${id}/presupuestos/${presupuestoId}/vincular`, { empresa_id, nodo_uid, tipo, crear }),
  ),
  desvincularPresupuesto: async (presupuestoId, empresa_id) => unwrap(
    await api.delete(`${BASE}/presupuestos/${presupuestoId}/vinculo`, { params: { empresa_id } }),
  ),
  // ── F5 · Moldes de obra ──────────────────────────────────────────────────
  moldes: async (empresa_id) => unwrap(await api.get(`${BASE}/moldes`, { params: { empresa_id } })),
  aplicarMolde: async (id, body) => unwrap(await api.post(`${BASE}/${id}/aplicar-molde`, body)),
  guardarComoMolde: async (id, body) => unwrap(await api.post(`${BASE}/${id}/guardar-como-molde`, body)),

  // ── Obra unificada · planilla de Presupuestos ────────────────────────────
  // Crea la obra del proyecto si no existe y le pone la plantilla, sin plata. Idempotente.
  // Con `precargar` crea además los presupuestos: { m2, valor_m2, moneda, incidencias: { [rubro]: pct } }.
  arrancarPresupuestos: async ({ empresa_id, proyecto_id, molde_id = null, ingresos = false, ...precarga }) => unwrap(
    await api.post(`${BASE}/arrancar`, { empresa_id, proyecto_id, molde_id, ingresos, ...precarga }),
  ),
  // Las cifras y los veredictos de todas las obras de la empresa, más el gastado de caja de los proyectos sin obra.
  resumenProyectos: async (empresa_id, monedas = ['ARS', 'USD']) => unwrap(
    await api.get(`${BASE}/resumen-proyectos`, { params: { empresa_id, monedas: monedas.join(',') } }),
  ),
  planilla: async (id, empresa_id) => unwrap(await api.get(`${BASE}/${id}/planilla`, { params: { empresa_id } })),
  // Devuelve la planilla ya recalculada más `rechazados: [{ indice, motivo }]`.
  escribirCeldasPlanilla: async (id, empresa_id, cambios) => unwrap(
    await api.post(`${BASE}/${id}/planilla/celdas`, { empresa_id, cambios }),
  ),
  // Estructura: abrir una fila, agregar filas (modo rubro | debajo | hija) y cambiar o ampliar la plantilla.
  abrirNodoPlanilla: async (id, empresa_id, nodo_uid) => unwrap(
    await api.post(`${BASE}/${id}/planilla/abrir`, { empresa_id, nodo_uid }),
  ),
  agregarFilaPlanilla: async (id, empresa_id, pedido) => unwrap(
    await api.post(`${BASE}/${id}/planilla/nodos`, { empresa_id, ...pedido }),
  ),
  cambiarPlantillaPlanilla: async (id, empresa_id, molde_id, modo) => unwrap(
    await api.post(`${BASE}/${id}/planilla/plantilla`, { empresa_id, molde_id, modo }),
  ),
  // Renombra (`nuevo_nombre`) o quita (`quitar: true`) en toda la obra las contingencias con ese nombre → { afectadas }.
  renombrarContingenciaPlanilla: async (id, empresa_id, { nombre, nuevo_nombre, quitar }) => unwrap(
    await api.patch(`${BASE}/${id}/planilla/contingencias`, { empresa_id, nombre, nuevo_nombre, quitar }),
  ),

  // ── C5 · Hitos ───────────────────────────────────────────────────────────
  // Todo lo que devuelven viene DERIVADO del backend: fecha, proyección y
  // cumplido. Acá no se calcula nada de eso.
  hitos: async (id, empresa_id) => unwrap(await api.get(`${BASE}/${id}/hitos`, { params: { empresa_id } })),
  crearHito: async (id, body) => unwrap(await api.post(`${BASE}/${id}/hitos`, body)),
  editarHito: async (id, hitoId, body) => unwrap(await api.patch(`${BASE}/${id}/hitos/${hitoId}`, body)),
  borrarHito: async (id, hitoId, empresa_id) => unwrap(await api.delete(`${BASE}/${id}/hitos/${hitoId}`, { params: { empresa_id } })),

  // ── F4 · La cola del copiloto ────────────────────────────────────────────
  // La cola entera y no una página: el panel agrupa por tema y necesita saber
  // cuántas son de verdad — decir "50 tareas" cuando son 76 es mentir.
  propuestas: async (id, empresa_id) => unwrap(await api.get(`${BASE}/${id}/propuestas`, { params: { empresa_id, limit: 500 } })),
  generarPropuestas: async (id, empresa_id) => unwrap(await api.post(`${BASE}/${id}/propuestas/generar`, { empresa_id })),
  resolverPropuesta: async (id, propuestaId, body) => unwrap(await api.patch(`${BASE}/${id}/propuestas/${propuestaId}`, body)),
  resolverTanda: async (id, body) => unwrap(await api.post(`${BASE}/${id}/propuestas/tanda`, body)),
  interpretarRespuesta: async (id, body) => unwrap(await api.post(`${BASE}/${id}/propuestas/interpretar`, body)),
  deshacerPropuestas: async (id, empresa_id, lote_id, user_id) => unwrap(await api.post(`${BASE}/${id}/propuestas/deshacer`, { empresa_id, lote_id, user_id })),

  /* ---------- Ajustes: qué controla la obra ---------- */

  /** Manda sólo lo que cambió; lo que no viaja, no se toca ni gana procedencia. */
  // `imputacion` sólo lleva la regla de reparto de cada lado: { egresos: { reparto } }.
  // `umbrales_semaforo`: { banda_pct?, atencion_pct?, banda_pts? }, sólo reemplaza los campos que viajan (uno en null vuelve a
  // heredar) · null hereda todo · sin pasar, no se toca.
  // `calendario_laboral`: { pesos_semana?, excepciones? }, cada parte reemplaza a la suya (pesos_semana: null
  // hereda; excepciones: [] las borra) y la que no viaja se conserva · null borra todo · sin pasar, no se toca.
  guardarAjustes: async (id, empresa_id, { control = null, ritmo = null, imputacion = null, calendario_laboral, terminadas, umbrales_semaforo, origen, ref, user_id } = {}) =>
    unwrap(await api.patch(`${BASE}/${id}/ajustes`, { empresa_id, control, ritmo, imputacion, calendario_laboral, terminadas, umbrales_semaforo, origen, ref, user_id })),

  /** Manda la consulta de avance ya: { enviados, motivo, resultados: [{ user_id, nombre, estado }] }. */
  enviarRitmoAhora: async (id, empresa_id) =>
    unwrap(await api.post(`${BASE}/${id}/ritmo/enviar-ahora`, { empresa_id })),

  /**
   * Qué pasaría con la salud y el cronograma si se declarara esto. No escribe nada.
   * `terminadas` ('mantener' | 'recalcular'): qué hacer con las tareas al 100 % si cambia el calendario.
   */
  simularAjustes: async (id, empresa_id, control, calendario_laboral, terminadas) =>
    unwrap(await api.post(`${BASE}/${id}/simular-ajustes`, { empresa_id, control, calendario_laboral, terminadas })),

  /* ---------- Calendario laboral de la empresa ---------- */
  // `calendario_laboral`: { pesos_semana?, excepciones? } | null (borra). Reemplaza al anterior entero.
  getCalendarioEmpresa: async (empresa_id) =>
    unwrap(await api.get(`${BASE}/empresa/${empresa_id}/calendario-laboral`)),

  /** Guarda y encola el recálculo de las obras en curso: { calendario_laboral, recalculo: { encolado, job_id } }. */
  guardarCalendarioEmpresa: async (empresa_id, calendario_laboral, terminadas) =>
    unwrap(await api.put(`${BASE}/empresa/${empresa_id}/calendario-laboral`, { calendario_laboral, terminadas })),

  /** Cuántas obras en curso y tareas se moverían: { obras_en_curso, obras_a_recalcular, tareas_movidas, tareas_terminadas }. No escribe. */
  simularCalendarioEmpresa: async (empresa_id, calendario_laboral, terminadas) =>
    unwrap(await api.post(`${BASE}/empresa/${empresa_id}/calendario-laboral/simular`, { calendario_laboral, terminadas })),

  /* ---------- Umbrales del semáforo de la empresa ---------- */
  /** `{ umbrales: { banda_pct, atencion_pct, banda_pts } | null, por_defecto }`. */
  getUmbralesEmpresa: async (empresa_id) =>
    unwrap(await api.get(`${BASE}/empresa/${empresa_id}/umbrales-semaforo`)),

  /** Reemplaza los umbrales de la empresa; un campo en null vuelve al default y `null` entero los borra. */
  guardarUmbralesEmpresa: async (empresa_id, umbrales) =>
    unwrap(await api.put(`${BASE}/empresa/${empresa_id}/umbrales-semaforo`, { umbrales })),

  registrarAvance: async (id, subrubroUid, empresa_id, avance_pct) => unwrap(await api.patch(`${BASE}/${id}/subrubros/${subrubroUid}/avance`, { empresa_id, avance_pct })),
  // `confirmar_montos` es la segunda pasada cuando el backend frena un monto de
  // otro orden de magnitud (mismo freno que la planilla, ahora también de a una).
  // `causa` es el motivo que el backend exige al re-fechar una tarea atrasada.
  editarSubrubro: async (id, subrubroUid, empresa_id, cambios, confirmar_montos = false, causa = null) => unwrap(await api.patch(`${BASE}/${id}/subrubros/${subrubroUid}`, { empresa_id, cambios, confirmar_montos, ...(causa ? { causa } : {}) })),
  // Editor masivo (planilla). `filas` = [{ nodo_uid, avance_pct?, fecha_inicio?,
  // fecha_fin?, monto?, costo_estimado?, costo_directo? }]. Todo o nada.
  guardarLoteSubrubros: async (id, empresa_id, filas, confirmar_montos = false, causa = null) => unwrap(await api.patch(`${BASE}/${id}/subrubros-lote`, { empresa_id, filas, confirmar_montos, ...(causa ? { causa } : {}) })),
  // Reparte una tarea en grupos de control. Viaja la lista COMPLETA: `[]`
  // deshace el reparto. Devuelve { obra, suma_pct } — la incidencia puede no dar
  // 100 y eso se avisa, no se rechaza.
  repartirEnGrupos: async (id, subrubroUid, empresa_id, grupos) => unwrap(await api.put(`${BASE}/${id}/subrubros/${subrubroUid}/grupos`, { empresa_id, grupos })),
  // Los grupos de control ya usados en la obra, con sus niveles intermedios.
  gruposDeControl: async (id, empresa_id) => unwrap(await api.get(`${BASE}/${id}/grupos`, { params: { empresa_id } })),
  // modo_avance: 'manual' | 'por_pagado' (derivado de lo gastado sobre el costo).
  setModoAvance: async (id, subrubroUid, empresa_id, modo_avance) => unwrap(await api.patch(`${BASE}/${id}/subrubros/${subrubroUid}/modo-avance`, { empresa_id, modo_avance })),
  // Rellena las categorías vacías desde el catálogo de la empresa. Sin `aplicar`
  // es un PREVIEW: devuelve qué asignaría sin tocar la obra.
  remapearCategorias: async (id, empresa_id, aplicar = false) => unwrap(await api.post(`${BASE}/${id}/remapear-categorias`, { empresa_id, aplicar })),
  eliminarSubrubro: async (id, subrubroUid, empresa_id) => unwrap(await api.delete(`${BASE}/${id}/subrubros/${subrubroUid}`, { params: { empresa_id } })),
  // Carga masiva de costo_estimado. direccion: 'costo_desde_contrato' | 'contrato_desde_costo'
  estimarCostos: async (id, empresa_id, { direccion, pct, solo_vacios = false }) => unwrap(await api.post(`${BASE}/${id}/estimar-costos`, { empresa_id, direccion, pct, solo_vacios })),
  // contrato = { proveedor_id, proveedor_nombre, monto, modalidad } | null (para borrar)
  asignarContrato: async (id, subrubroUid, empresa_id, contrato) => unwrap(await api.patch(`${BASE}/${id}/subrubros/${subrubroUid}/contrato`, { empresa_id, contrato })),
  // responsable = { user_id, nombre } | null (para borrar)
  asignarResponsable: async (id, subrubroUid, empresa_id, responsable) => unwrap(await api.patch(`${BASE}/${id}/subrubros/${subrubroUid}/responsable`, { empresa_id, responsable })),

  /* ---------- CRUD de estructura + auditoría ---------- */
  agregarRubro: async (id, empresa_id, nombre) => unwrap(await api.post(`${BASE}/${id}/rubros`, { empresa_id, nombre })),
  editarRubro: async (id, rubroUid, empresa_id, nombre) => unwrap(await api.patch(`${BASE}/${id}/rubros/${rubroUid}`, { empresa_id, nombre })),
  // Regla de reparto del nodo: `{ egresos?, ingresos? }`, null = hereda de la obra.
  // El rubro se edita por su propio endpoint; subrubro e ítem, por el de subrubros.
  setRepartoNodo: async (id, { uid, nivel }, empresa_id, reparto) => unwrap(await (nivel === 1
    ? api.patch(`${BASE}/${id}/rubros/${uid}`, { empresa_id, reparto })
    : api.patch(`${BASE}/${id}/subrubros/${uid}`, { empresa_id, cambios: { reparto } }))),
  eliminarRubro: async (id, rubroUid, empresa_id) => unwrap(await api.delete(`${BASE}/${id}/rubros/${rubroUid}`, { params: { empresa_id } })),
  agregarSubrubro: async (id, rubroUid, empresa_id, data) => unwrap(await api.post(`${BASE}/${id}/rubros/${rubroUid}/subrubros`, { empresa_id, ...data })),
  // Tercer nivel. El ítem cuelga de una tarea; para borrarlo se usa
  // `eliminarSubrubro`, que resuelve el nodo sea del nivel que sea.
  agregarItem: async (id, subrubroUid, empresa_id, data) => unwrap(await api.post(`${BASE}/${id}/subrubros/${subrubroUid}/items`, { empresa_id, ...data })),
  // Profundidad del plan: 1 (sólo rubros), 2 (rubro + tareas) o 3 (con ítems).
  setNiveles: async (id, empresa_id, niveles) => unwrap(await api.patch(`${BASE}/${id}/niveles`, { empresa_id, niveles })),
  /** Con qué detalle se lleva el cronograma de la obra ('subrubro' | 'item'). `causa` si mueve tareas atrasadas. */
  // `lado`: 'tiempo' | 'ingresos' | 'egresos'. `nodo_uid` = la rama que pisa el nivel de la obra (nulo = la obra).
  cambiarDetalle: async (id, empresa_id, nivel, causa = null, { lado = 'tiempo', nodo_uid = null } = {}) =>
    unwrap(await api.patch(`${BASE}/${id}/detalle`, {
      empresa_id, lado, nivel, ...(nodo_uid ? { nodo_uid } : {}), ...(causa ? { causa } : {}),
    })),
  /**
   * Lo que pasaría al cambiar el cronograma a `nivel`, sin guardar: { nivel_actual, nivel_nuevo, cambia, heredan, vuelven_a_tarea,
   * movidas, dependencias_movidas, atrasadas, fin_antes, fin_despues, dias_fin_obra }.
   */
  simularDetalle: async (id, empresa_id, nivel, { lado = 'tiempo', nodo_uid = null } = {}) =>
    unwrap(await api.post(`${BASE}/${id}/detalle/simular`, { empresa_id, lado, nivel, ...(nodo_uid ? { nodo_uid } : {}) })),
  auditoria: async (id, empresa_id) => {
    const res = await api.get(`${BASE}/${id}/auditoria`, { params: { empresa_id } });
    return Array.isArray(res.data?.items) ? res.data.items : [];
  },
  // La misma historia con un lote por línea (y sus filas adentro); los registros viejos salen como siempre.
  auditoriaPorLote: async (id, empresa_id) => {
    const res = await api.get(`${BASE}/${id}/auditoria`, { params: { empresa_id, agrupado: 1 } });
    return Array.isArray(res.data?.items) ? res.data.items : [];
  },

  /* ---------- Estructura: unificar un rubro con su subrubro único ---------- */
  // { obra, situaciones[{ rubro_uid, estado, subrubro_uid, subrubro_nombre, bloqueos[] }], resumen }
  diagnosticoDeEstructura: async (id, empresa_id) =>
    unwrap(await api.get(`${BASE}/${id}/estructura/diagnostico`, { params: { empresa_id } })),
  // dry_run (default): { unificados, rechazados, simulacion }. Aplicar: { lote_id, unificados, rechazados }.
  // Obra cambiada desde la vista previa (409): devuelve { cambio: true, simulacion } en vez de lanzar.
  unificarRubro: async (id, empresa_id, { rubro_uid, nombre_final = null, dry_run = true, huella = null }) => {
    try {
      return unwrap(await api.post(`${BASE}/${id}/estructura/unificar`, {
        empresa_id, rubros: [{ rubro_uid, ...(nombre_final ? { nombre_final } : {}) }], dry_run, ...(huella ? { huella } : {}),
      }));
    } catch (e) {
      const err = e?.response?.data?.error;
      if (e?.response?.status === 409 && err?.cambio) return { cambio: true, simulacion: err.simulacion };
      const nuevo = new Error(err?.message || e?.message || 'No se pudo unificar');
      nuevo.bloqueos = err?.bloqueos || null;
      throw nuevo;
    }
  },
  // { lote_id, revertidos, no_revertidos }; con `rubro_uids` solo esos rubros.
  deshacerLote: async (id, empresa_id, lote_id, rubro_uids = null) => {
    try {
      return unwrap(await api.post(`${BASE}/${id}/cambios/deshacer`, { empresa_id, lote_id, ...(rubro_uids ? { rubro_uids } : {}) }));
    } catch (e) {
      throw new Error(e?.response?.data?.error?.message || e?.message || 'No se pudo deshacer');
    }
  },

  /**
   * P5 — La caja de la obra mes a mes, de hoy en adelante.
   *
   * Cruza las tres fuentes que ya existían y nunca se habían mirado juntas: el
   * cronograma (cuándo se ejecuta cada tarea), los planes de cobro (cuándo entra
   * la plata comprometida) y los certificados emitidos sin cobrar.
   */
  proyecciones: async (id, empresa_id) => {
    const res = await api.get(`${BASE}/${id}/proyecciones`, { params: { empresa_id } });
    if (res.status !== 200) throw new Error('Error al obtener las proyecciones');
    return unwrap(res);
  },

  curvaS: async (id, empresa_id) => {
    const res = await api.get(`${BASE}/${id}/curva-s`, { params: { empresa_id } });
    if (res.status === 200) return unwrap(res);
    return { meses: [], certificado: [], cobrado: [], plan: [] };
  },

  // El informe del plan: la previsualización es JSON y el PDF se arma en el servidor.
  informe: async (id, params) => unwrap(await api.get(`${BASE}/${id}/informe`, { params })),
  descargarInformePdf: async (id, params) => {
    let res;
    try {
      res = await api.get(`${BASE}/${id}/informe.pdf`, { params, responseType: 'blob' });
    } catch (e) {
      // Con `blob` hasta el JSON de error llega como Blob: se lee para que el mensaje real llegue a la pantalla.
      if (e?.response?.data instanceof Blob) {
        try { e.response.data = JSON.parse(await e.response.data.text()); } catch { /* queda el error genérico */ }
      }
      throw e;
    }
    const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
    const a = document.createElement('a');
    a.href = url; a.download = `informe-plan-${params.perfil}.pdf`;
    document.body.appendChild(a); a.click(); a.remove();
    window.URL.revokeObjectURL(url);
  },

  // El link fijo del informe para el cliente: el token solo viaja al crear o regenerar.
  informePublico: async (id, empresa_id) => unwrap(await api.get(`${BASE}/${id}/informe-publico`, { params: { empresa_id } })),
  actualizarInformePublico: async (id, empresa_id, body) => unwrap(await api.post(`${BASE}/${id}/informe-publico`, body, { params: { empresa_id } })),
  regenerarInformePublico: async (id, empresa_id) => unwrap(await api.post(`${BASE}/${id}/informe-publico/regenerar`, {}, { params: { empresa_id } })),
  desactivarInformePublico: async (id, empresa_id) => unwrap(await api.delete(`${BASE}/${id}/informe-publico`, { params: { empresa_id } })),

  // Ida y vuelta con Excel. La descarga es binaria: hay que pedir `blob` o axios
  // devuelve el xlsx interpretado como texto y el archivo sale corrupto.
  exportarObra: async (id, empresa_id) => {
    const res = await api.get(`${BASE}/${id}/exportar`, { params: { empresa_id }, responseType: 'blob' });
    const cd = res.headers?.['content-disposition'] || '';
    const m = /filename\*=UTF-8''([^;]+)/.exec(cd);
    const nombre = m ? decodeURIComponent(m[1]) : 'obra.xlsx';
    const url = URL.createObjectURL(new Blob([res.data]));
    const a = document.createElement('a');
    a.href = url; a.download = nombre; a.click();
    URL.revokeObjectURL(url);
    return nombre;
  },

  // Sin `aplicar` es un PREVIEW: devuelve el diff sin tocar la obra.
  importarPlanilla: async (id, empresa_id, archivo, aplicar = false, confirmar_montos = false) => {
    const fd = new FormData();
    fd.append('archivo', archivo);
    fd.append('empresa_id', empresa_id);
    fd.append('aplicar', aplicar ? 'true' : 'false');
    fd.append('confirmar_montos', confirmar_montos ? 'true' : 'false');
    return unwrap(await api.post(`${BASE}/${id}/importar-planilla`, fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }));
  },

  // C1 — Impacto de mover una tarea, sin guardar: a cuántas arrastra y cuánto
  // corre el fin de obra.
  simularMover: async (id, empresa_id, nodo_uid, fechas) =>
    unwrap(await api.post(`${BASE}/${id}/simular-mover`, { empresa_id, nodo_uid, ...fechas })),

  // C2 — Congela las fechas actuales como línea base. { limpiar: true } la borra.
  aprobarCronograma: async (id, empresa_id, limpiar = false) => unwrap(await api.post(`${BASE}/${id}/aprobar-cronograma`, { empresa_id, limpiar })),

  cronograma: async (id, empresa_id) => {
    const res = await api.get(`${BASE}/${id}/cronograma`, { params: { empresa_id } });
    if (res.status === 200) return unwrap(res);
    return { rango: { min: null, max: null }, rubros: [] };
  },

  /* ---------- Cartera (cross-obra) ---------- */
  resumenCartera: async (empresa_id, { incluir_archivadas = false } = {}) => {
    const res = await api.get(`${BASE}/cartera/resumen`, { params: { empresa_id, incluir_archivadas: incluir_archivadas ? 'true' : undefined } });
    if (res.status !== 200) throw new Error('Error al obtener la cartera');
    return Array.isArray(res.data?.items) ? res.data.items : [];
  },

  /* ---------- Archivar / eliminar obra ---------- */
  archivarObra: async (id, empresa_id) => unwrap(await api.patch(`${BASE}/${id}/archivar`, { empresa_id })),
  desarchivarObra: async (id, empresa_id) => unwrap(await api.patch(`${BASE}/${id}/desarchivar`, { empresa_id })),
  eliminarObra: async (id, empresa_id) => unwrap(await api.delete(`${BASE}/${id}`, { params: { empresa_id } })),

  cobranzas: async (empresa_id) => {
    const res = await api.get(`${BASE}/cartera/cobranzas`, { params: { empresa_id } });
    if (res.status !== 200) throw new Error('Error al obtener cobranzas');
    return Array.isArray(res.data?.items) ? res.data.items : [];
  },

  pagosCartera: async (empresa_id) => {
    const res = await api.get(`${BASE}/cartera/pagos`, { params: { empresa_id } });
    if (res.status !== 200) throw new Error('Error al obtener pagos');
    return Array.isArray(res.data?.items) ? res.data.items : [];
  },

  // `cobro` acepta lo que arma el diálogo de cobro: monto_parcial, fecha_cobrado,
  // modo, movimiento_id, cobro_total_confirmado (cuota totalmente pagada) y motivo_diferencia.
  cobrarCuota: async (obraId, cuotaId, empresa_id, cobro = {}) =>
    unwrap(await api.post(`${BASE}/${obraId}/cuotas/${cuotaId}/cobrar`, { empresa_id, ...cobro })),

  /* ---------- Costo y margen (Fase 2) ---------- */
  ejecucion: async (obraId, empresa_id, lente_tiempo = null) => {
    const res = await api.get(`${BASE}/${obraId}/ejecucion`, {
      params: { empresa_id, ...(lente_tiempo ? { lente_tiempo } : {}) },
    });
    if (res.status === 200) return unwrap(res);
    throw new Error('Error al obtener la ejecución');
  },

  egresosCandidatos: async (obraId, empresa_id) => {
    const res = await api.get(`${BASE}/${obraId}/egresos-candidatos`, { params: { empresa_id } });
    if (res.status !== 200) throw new Error('Error al obtener egresos');
    return Array.isArray(res.data?.items) ? res.data.items : [];
  },

  // Cobros ya cargados en la caja del proyecto y todavía sin vincular a la obra (T1).
  ingresosCandidatos: async (obraId, empresa_id) => {
    const res = await api.get(`${BASE}/${obraId}/ingresos-candidatos`, { params: { empresa_id } });
    if (res.status !== 200) throw new Error('Error al obtener ingresos');
    return Array.isArray(res.data?.items) ? res.data.items : [];
  },

  /**
   * Lo mismo, pero con el tamaño real del trabajo.
   *
   * Los dos de arriba devuelven el array pelado porque alimentan autocompletes,
   * donde 50 filas alcanzan. La bandeja de sin imputar necesita además cuántos
   * quedan y por cuánta plata: es una lista que tiene que vaciarse.
   */
  sinImputar: async (obraId, empresa_id, { tipo = 'egreso', limit = 200 } = {}) => {
    const ruta = tipo === 'ingreso' ? 'ingresos-candidatos' : 'egresos-candidatos';
    const res = await api.get(`${BASE}/${obraId}/${ruta}`, { params: { empresa_id, limit } });
    if (res.status !== 200) throw new Error('Error al obtener los movimientos sin imputar');
    return {
      items: Array.isArray(res.data?.items) ? res.data.items : [],
      total: Number(res.data?.total || 0),
      monto: Number(res.data?.monto || 0),
    };
  },

  // Sirve para egresos e ingresos: el backend decide por el tipo del movimiento.
  // W4 — Candidatos agrupados por proveedor/categoría: 6 decisiones en vez de 200.
  reimputarAgrupado: async (obraId, empresa_id) => {
    const res = await api.get(`${BASE}/${obraId}/reimputar-agrupado`, { params: { empresa_id } });
    return {
      items: Array.isArray(res.data?.items) ? res.data.items : [],
      // Cuántos entraron en esta pasada y cuántos hay sin imputar en total: el
      // agrupado corta en `limite` y sin esto el recorte no se ve.
      analizados: Number(res.data?.analizados || 0),
      total: Number(res.data?.total || 0),
      limite: Number(res.data?.limite || 0),
    };
  },
  // Persiste "lo de este proveedor va a esta tarea" en criterios.proveedor_ids.
  guardarReglaProveedor: async (obraId, empresa_id, nodo_uid, proveedor_id) =>
    unwrap(await api.post(`${BASE}/${obraId}/regla-proveedor`, { empresa_id, nodo_uid, proveedor_id })),

  imputarGasto: async (obraId, data) => unwrap(await api.post(`${BASE}/${obraId}/imputar`, data)),
  /** Edición masiva del rubro desde la caja: { empresa_id, movimiento_ids, nodo_uid|null } → { total, aplicados, results }. */
  imputarLote: async (obraId, data) => unwrap(await api.post(`${BASE}/${obraId}/imputar-lote`, data)),

  /**
   * Vista previa del reparto de un gasto apuntado a un nodo con hijos: hijos,
   * regla por defecto (y de qué nivel viene) y las líneas con su monto. Sin
   * `regla` aplica la default; si la default es «preguntar», no trae líneas.
   */
  previsualizarReparto: async (obraId, { empresa_id, nodo_uid, lado = 'egresos', regla = null, monto = 0 }) =>
    unwrap(await api.get(`${BASE}/${obraId}/reparto`, {
      params: { empresa_id, nodo_uid, lado, monto, ...(regla ? { regla } : {}) },
    })),

  /**
   * Repartir, volver a repartir o devolver al padre un gasto ya imputado.
   * `regla`: 'padre' | 'costo' | 'iguales' | 'manual' (con `pcts` [{ nodo_uid, pct }]).
   */
  rehacerReparto: async (obraId, movId, { empresa_id, origen_nodo_uid, regla, pcts = [] }) =>
    unwrap(await api.post(`${BASE}/${obraId}/reparto/${movId}`, {
      empresa_id, origen_nodo_uid, regla, pcts,
    })),

  /**
   * A qué nodo iría un gasto que todavía se está cargando.
   *
   * Corre el mismo matcher que usa la re-imputación masiva, pero contra el
   * borrador del formulario — que ya tiene proveedor, categoría y subcategoría.
   */
  sugerirNodo: async (obraId, empresa_id, movimiento) =>
    unwrap(await api.post(`${BASE}/${obraId}/sugerir-nodo`, { empresa_id, movimiento })),

  // Re-imputación masiva (T1b): preview con sugerencias del matcher + aplicar en lote.
  reimputarLote: async (obraId, empresa_id, limit) => {
    const res = await api.get(`${BASE}/${obraId}/reimputar-lote`, { params: { empresa_id, ...(limit ? { limit } : {}) } });
    if (res.status !== 200) throw new Error('Error al obtener el lote a re-imputar');
    return Array.isArray(res.data?.items) ? res.data.items : [];
  },
  reimputarAplicar: async (obraId, data) => unwrap(await api.post(`${BASE}/${obraId}/reimputar-aplicar`, data)),

  // Movimientos (egresos + ingresos) imputados a la obra, filtrables por sub-rubro.
  movimientosImputados: async (obraId, empresa_id, nodo_uid) => {
    const res = await api.get(`${BASE}/${obraId}/movimientos-imputados`, { params: { empresa_id, ...(nodo_uid ? { nodo_uid } : {}) } });
    if (res.status !== 200) throw new Error('Error al obtener los movimientos imputados');
    return Array.isArray(res.data?.items) ? res.data.items : [];
  },

  /* ---------- Mano de obra (Fase 3) ---------- */
  listarOrdenes: async (obraId, empresa_id) => {
    const res = await api.get(`${BASE}/${obraId}/ordenes-pago`, { params: { empresa_id } });
    return Array.isArray(res.data?.items) ? res.data.items : [];
  },
  crearOrden: async (obraId, data) => unwrap(await api.post(`${BASE}/${obraId}/ordenes-pago`, data)),
  ordenesSugeridas: async (obraId, empresa_id) => {
    const res = await api.get(`${BASE}/${obraId}/ordenes-sugeridas`, { params: { empresa_id } });
    return Array.isArray(res.data?.items) ? res.data.items : [];
  },
  generarOrdenSugerida: async (obraId, empresa_id, proveedor_key) => unwrap(await api.post(`${BASE}/${obraId}/ordenes-sugeridas/generar`, { empresa_id, proveedor_key })),
  aprobarOrden: async (ordenId, empresa_id) => unwrap(await api.post(`${BASE}/ordenes-pago/${ordenId}/aprobar`, { empresa_id })),
  pagarOrden: async (ordenId, empresa_id) => unwrap(await api.post(`${BASE}/ordenes-pago/${ordenId}/pagar`, { empresa_id })),
  anularOrden: async (ordenId, empresa_id) => unwrap(await api.post(`${BASE}/ordenes-pago/${ordenId}/anular`, { empresa_id })),

  /* ---------- Reportes / conforme (Fase 4) ---------- */
  listarInconvenientes: async (obraId, empresa_id) => {
    const res = await api.get(`${BASE}/${obraId}/inconvenientes`, { params: { empresa_id } });
    return Array.isArray(res.data?.items) ? res.data.items : [];
  },
  crearInconveniente: async (obraId, data) => unwrap(await api.post(`${BASE}/${obraId}/inconvenientes`, data)),
  resolverInconveniente: async (incId, empresa_id, resolucion) => unwrap(await api.post(`${BASE}/inconvenientes/${incId}/resolver`, { empresa_id, resolucion })),
  /* ---------- Papeles: planos, actas, fotos (C4) ---------- */
  // El archivo viaja crudo al backend, que lo sube a Storage y devuelve la URL.
  // `destino` es 'obra', 'inconveniente' o 'comentario'.
  subirAdjunto: async ({ destino = 'obra', id, empresa_id, archivo, nombre = null }) => {
    const fd = new FormData();
    fd.append('empresa_id', empresa_id);
    fd.append('archivo', archivo);
    if (nombre) fd.append('nombre', nombre);
    return unwrap(await api.post(rutaAdjuntos(destino, id), fd, { headers: { 'Content-Type': 'multipart/form-data' } }));
  },

  borrarAdjunto: async ({ destino = 'obra', id, empresa_id, adjunto_id }) => {
    return unwrap(await api.delete(`${rutaAdjuntos(destino, id)}/${adjunto_id}`, { data: { empresa_id } }));
  },

  /* ---------- Comentarios del nodo ---------- */
  listarComentarios: async (obraId, empresa_id, nodo_uid) => {
    const res = await api.get(`${BASE}/${obraId}/comentarios`, { params: { empresa_id, ...(nodo_uid ? { nodo_uid } : {}) } });
    return Array.isArray(res.data?.items) ? res.data.items : [];
  },
  // { [nodo_uid]: n } para el contador del árbol.
  conteoComentarios: async (obraId, empresa_id) => {
    const res = await api.get(`${BASE}/${obraId}/comentarios/conteo`, { params: { empresa_id } });
    return res.data?.por_nodo || res.data?.data?.por_nodo || {};
  },
  crearComentario: async (obraId, data) => unwrap(await api.post(`${BASE}/${obraId}/comentarios`, data)),
  editarComentario: async (comentarioId, empresa_id, texto) =>
    unwrap(await api.patch(`${BASE}/comentarios/${comentarioId}`, { empresa_id, texto })),
  borrarComentario: async (comentarioId, empresa_id) =>
    unwrap(await api.delete(`${BASE}/comentarios/${comentarioId}`, { data: { empresa_id } })),
  convertirComentario: async (comentarioId, data) =>
    unwrap(await api.post(`${BASE}/comentarios/${comentarioId}/convertir`, data)),

  // La línea de tiempo de la obra, acotada a un nodo (y, con `conRama`, a sus descendientes).
  lineaDeTiempoDelNodo: async (obraId, empresa_id, nodo_uid, { conRama = false } = {}) => {
    const res = await api.get(`${BASE}/${obraId}/auditoria`, {
      params: { empresa_id, nodo_uid, ...(conRama ? { con_rama: 1 } : {}) },
    });
    return Array.isArray(res.data?.items) ? res.data.items : [];
  },

  emitirConforme: async (obraId, empresa_id, checklist) => unwrap(await api.post(`${BASE}/${obraId}/conforme`, { empresa_id, checklist })),

  /* ---------- Certificados ---------- */
  listarCertificados: async (obraId, empresa_id) => {
    const res = await api.get(`${BASE}/${obraId}/certificados`, { params: { empresa_id } });
    if (res.status !== 200) throw new Error('Error al listar certificados');
    return Array.isArray(res.data?.items) ? res.data.items : [];
  },

  crearCertificado: async (obraId, data) => {
    const res = await api.post(`${BASE}/${obraId}/certificados`, data);
    if (res.status === 200 || res.status === 201) return unwrap(res);
    throw new Error('No se pudo crear el certificado');
  },

  // `fecha` sólo viaja si se pasa: el backend distingue `undefined` (no tocar)
  // de null (volver a hoy), y mandar null sin querer le corre la fecha.
  editarCertificado: async (certId, empresa_id, lineas, fecha = undefined) =>
    unwrap(await api.patch(`${BASE}/certificados/${certId}`, { empresa_id, lineas, fecha })),
  agregarAjusteCertificado: async (certId, empresa_id, ajuste) =>
    unwrap(await api.post(`${BASE}/certificados/${certId}/ajustes`, { empresa_id, ...ajuste })),
  editarAjusteCertificado: async (certId, grupoId, empresa_id, cambios) =>
    unwrap(await api.patch(`${BASE}/certificados/${certId}/ajustes/${grupoId}`, { empresa_id, ...cambios })),
  quitarAjusteCertificado: async (certId, grupoId, empresa_id) =>
    unwrap(await api.delete(`${BASE}/certificados/${certId}/ajustes/${grupoId}`, { params: { empresa_id } })),
  sugerenciaAjuste: async (certId, empresa_id, tipo, fecha_base) =>
    unwrap(await api.get(`${BASE}/certificados/${certId}/sugerencia-ajuste`, {
      params: { empresa_id, tipo, ...(fecha_base ? { fecha_base } : {}) },
    })),
  descargarActa: async (certId, empresa_id, numero) => {
    const res = await api.get(`${BASE}/certificados/${certId}/acta`, { params: { empresa_id }, responseType: 'blob' });
    const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
    const a = document.createElement('a');
    a.href = url; a.download = `certificado-${numero || certId}.pdf`;
    document.body.appendChild(a); a.click(); a.remove();
    window.URL.revokeObjectURL(url);
  },
  enviarCertificado: async (certId, empresa_id) => unwrap(await api.post(`${BASE}/certificados/${certId}/enviar`, { empresa_id })),
  aprobarCertificado: async (certId, empresa_id) => unwrap(await api.post(`${BASE}/certificados/${certId}/aprobar`, { empresa_id })),
  rechazarCertificado: async (certId, empresa_id, motivo) => unwrap(await api.post(`${BASE}/certificados/${certId}/rechazar`, { empresa_id, motivo })),
  anularCertificado: async (certId, empresa_id, motivo) => unwrap(await api.post(`${BASE}/certificados/${certId}/anular`, { empresa_id, motivo })),
  cobrarCertificado: async (certId, empresa_id, montoParcial = null, fechaCobrado = null) =>
    unwrap(await api.post(`${BASE}/certificados/${certId}/cobrar`, { empresa_id, monto_parcial: montoParcial, fecha_cobrado: fechaCobrado })),

  // Multi-plan: crear/asociar/desasociar planes de cobro de la obra (0..N).
  // `plan_cobro_id` presente → asocia uno existente; ausente → crea uno nuevo.
  agregarPlan: async (obraId, empresa_id, {
    plan_cobro_id = null, nombre = null, monto_total = 0,
    // El plan es el acuerdo con quien financia: modalidad 'certificacion' = cobra
    // contra avance, y sus cuotas las pare cada certificado aprobado.
    modalidad = null, cliente_nombre = null, periodo = null, dia_corte = null, plazo_pago_dias = 0,
  } = {}) =>
    unwrap(await api.post(`${BASE}/${obraId}/planes`, {
      empresa_id, plan_cobro_id, nombre, monto_total,
      modalidad, cliente_nombre, periodo, dia_corte, plazo_pago_dias,
    })),
  desasociarPlan: async (obraId, empresa_id, planId) =>
    unwrap(await api.delete(`${BASE}/${obraId}/planes/${planId}`, { params: { empresa_id } })),

  /* ---------- Planes de pago a proveedores (T3, lado costo) ----------
     Espejo outbound de los planes de cobro: 0..N por obra, cada uno con sus
     cuotas + resumen embebidos por el backend (getPlanes ya devuelve ambos). */
  listarPlanesPago: async (obraId, empresa_id) => {
    const res = await api.get(`${BASE}/${obraId}/planes-pago`, { params: { empresa_id } });
    return Array.isArray(res.data?.items) ? res.data.items : [];
  },
  crearPlanPago: async (obraId, data) => unwrap(await api.post(`${BASE}/${obraId}/planes-pago`, data)),
  // Plan de pago suelto (sin obra): requiere proyecto_id (caja destino) y monto_total.
  crearPlanPagoEmpresa: async (data) => unwrap(await api.post(`${BASE}/planes-pago`, data)),

  // Detalle de un plan de pago (con cuotas + resumen embebidos).
  getPlanPago: async (planId, empresa_id) => unwrap(await api.get(`${BASE}/planes-pago/${planId}`, { params: { empresa_id } })),
  editarPlanPago: async (planId, empresa_id, cambios) =>
    unwrap(await api.patch(`${BASE}/planes-pago/${planId}`, { empresa_id, cambios })),
  eliminarPlanPago: async (planId, empresa_id) =>
    unwrap(await api.delete(`${BASE}/planes-pago/${planId}`, { params: { empresa_id } })),

  // Cuotas del plan de pago.
  // data = { empresa_id, tipo?, descripcion?, fecha_vencimiento?, monto, nodo_uids?, hito_pct? }
  agregarCuotaPago: async (planId, data) => unwrap(await api.post(`${BASE}/planes-pago/${planId}/cuotas`, data)),
  // data = { empresa_id, monto, cantidad, frecuencia?, fecha_inicio, descripcion? }
  generarCuotasPeriodicas: async (planId, data) =>
    unwrap(await api.post(`${BASE}/planes-pago/${planId}/cuotas/periodicas`, data)),
  generarCuotasPorAvance: async (planId, empresa_id, fecha_vencimiento = null) =>
    unwrap(await api.post(`${BASE}/planes-pago/${planId}/cuotas/por-avance`, { empresa_id, fecha_vencimiento })),
  editarCuotaPago: async (planId, cuotaId, empresa_id, cambios) =>
    unwrap(await api.patch(`${BASE}/planes-pago/${planId}/cuotas/${cuotaId}`, { empresa_id, cambios })),
  eliminarCuotaPago: async (planId, cuotaId, empresa_id) =>
    unwrap(await api.delete(`${BASE}/planes-pago/${planId}/cuotas/${cuotaId}`, { params: { empresa_id } })),
  aprobarCuotaPago: async (planId, cuotaId, empresa_id) =>
    unwrap(await api.post(`${BASE}/planes-pago/${planId}/cuotas/${cuotaId}/aprobar`, { empresa_id })),
  // monto_parcial null => paga el saldo total. fecha_pago opcional.
  pagarCuotaPago: async (planId, cuotaId, empresa_id, { monto_parcial = null, fecha_pago = null } = {}) =>
    unwrap(await api.post(`${BASE}/planes-pago/${planId}/cuotas/${cuotaId}/pagar`, { empresa_id, monto_parcial, fecha_pago })),
  revertirPagoCuota: async (planId, cuotaId, empresa_id) =>
    unwrap(await api.post(`${BASE}/planes-pago/${planId}/cuotas/${cuotaId}/revertir`, { empresa_id })),

  // Cartera cross-obra (T6): cuotas de pago a proveedores pendientes de TODAS las obras
  // de la empresa. El backend devuelve una fila por cuota; la hoja standalone las
  // agrupa por plan. Espejo del listado de planes de cobro.
  listarPlanesPagoCartera: async (empresa_id) => {
    const res = await api.get(`${BASE}/cartera/pagos-plan`, { params: { empresa_id } });
    if (res.status !== 200) throw new Error('Error al obtener los planes de pago');
    return Array.isArray(res.data?.items) ? res.data.items : [];
  },
  // TODOS los planes de pago de la empresa (cross-obra, uno por plan con resumen + obra_titulo).
  listarPlanesPagoEmpresa: async (empresa_id) => {
    const res = await api.get(`${BASE}/cartera/planes-pago`, { params: { empresa_id } });
    if (res.status !== 200) throw new Error('Error al obtener los planes de pago');
    return Array.isArray(res.data?.items) ? res.data.items : [];
  },
};

export default ControlObraService;
